import React, { useState, useEffect, useCallback } from 'react';
import { t } from '../i18n';
import Layout from '../components/Layout';
import { ordersByDayAPI } from '../api';
import toast from 'react-hot-toast';

// ─── Заказы по дням ─────────────────────────────────────────────────────────
//
// Вопрос «сколько и каких заказов приняли за период» приходит в разных
// разрезах: по дням, по ЛДСП, по контрагенту, по менеджеру — и в любом
// порядке, вплоть до «без группировки, просто список». Поэтому разрез
// выбирается при формировании, а не зашит в экран.
//
// Число заказов считает сервер по distinct: при группировке по ЛДСП заказ
// с двумя плитами попадает в обе группы, но в итоге считается один раз.

const CARD = {
  background: 'var(--card)', border: '1px solid var(--border)',
  borderRadius: 14, padding: 16,
};
const FIELD = {
  background: 'var(--bg)', color: 'var(--text)', border: '1px solid var(--border)',
  borderRadius: 8, padding: '8px 10px', fontSize: 14, fontFamily: 'inherit',
  minWidth: 0, maxWidth: '100%',
};
const LBL = { fontSize: 12, color: 'var(--muted)', marginBottom: 4, display: 'block' };

const DIMS = [
  { id: 'day', name: 'День' },
  { id: 'product', name: 'ЛДСП' },
  { id: 'client', name: 'Контрагент' },
  { id: 'manager', name: 'Менеджер' },
];

const pad = (n) => String(n).padStart(2, '0');
const iso = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

function monthRange() {
  const now = new Date();
  return [iso(new Date(now.getFullYear(), now.getMonth(), 1)), iso(now)];
}

const NUM = { textAlign: 'right', whiteSpace: 'nowrap' };
const TH = {
  textAlign: 'left', fontSize: 12, color: 'var(--muted)', fontWeight: 600,
  padding: '8px 10px', borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap',
};
const TD = { padding: '7px 10px', fontSize: 13.5, borderBottom: '1px solid var(--border)' };

function ItemRow({ r, depth }) {
  return (
    <tr>
      <td style={{ ...TD, paddingLeft: 10 + depth * 18, whiteSpace: 'nowrap' }}>
        {r.created_at}
      </td>
      <td style={{ ...TD, whiteSpace: 'nowrap' }}>{r.order_number}</td>
      <td style={TD}>{r.client}</td>
      <td style={TD}>{r.manager}</td>
      <td style={TD}>{r.product}<span style={{ color: 'var(--muted)' }}> · {r.texture}</span></td>
      <td style={{ ...TD, ...NUM }}>{r.quantity}</td>
      <td style={{ ...TD, ...NUM }}>{r.done}</td>
      <td style={{ ...TD, ...NUM }}>{r.shipped}</td>
      <td style={{ ...TD, whiteSpace: 'nowrap' }}>
        {t(r.status_label)}
        {r.is_service && (
          <span style={{
            marginLeft: 6, background: '#f59e0b22', color: '#f59e0b',
            borderRadius: 10, padding: '1px 8px', fontSize: 11.5, fontWeight: 700,
          }}>{t('Услуга')}</span>
        )}
      </td>
    </tr>
  );
}

// Группа: строка-итог и, если раскрыта, вложенное. Колонки у групп и у
// позиций разные, поэтому итог группы лежит в той же таблице отдельной
// шапкой на всю ширину, а числа — в правых колонках.
function Node({ node, depth, path, open, toggle }) {
  const isOpen = open.has(path);
  const kids = node.children
    ? node.children.map(c => (
      <Node key={c.key} node={c} depth={depth + 1} path={path + '/' + c.key}
            open={open} toggle={toggle} />))
    : (node.rows || []).map((r, i) => <ItemRow key={i} r={r} depth={depth + 1} />);
  const tot = node.totals;
  return (
    <>
      <tr onClick={() => toggle(path)} style={{
        cursor: 'pointer', background: depth === 0 ? 'var(--bg)' : 'transparent',
      }}>
        <td colSpan={5} style={{
          ...TD, paddingLeft: 10 + depth * 18, fontWeight: 700,
        }}>
          <span style={{ display: 'inline-block', width: 16 }}>{isOpen ? '▾' : '▸'}</span>
          <span style={{ color: 'var(--muted)', fontWeight: 400 }}>
            {t(DIMS.find(d => d.id === node.level)?.name || '')}:{' '}
          </span>
          {node.label}
        </td>
        <td style={{ ...TD, ...NUM, fontWeight: 700 }}>{tot.quantity}</td>
        <td style={{ ...TD, ...NUM, fontWeight: 700 }}>{tot.done}</td>
        <td style={{ ...TD, ...NUM, fontWeight: 700 }}>{tot.shipped}</td>
        <td style={{ ...TD, fontWeight: 700, whiteSpace: 'nowrap' }}>
          {t('Заказов')}: {tot.orders} · {t('Позиций')}: {tot.items}
        </td>
      </tr>
      {isOpen && kids}
    </>
  );
}

function collectPaths(nodes, base, out) {
  (nodes || []).forEach(n => {
    const p = base + '/' + n.key;
    out.push(p);
    collectPaths(n.children, p, out);
  });
  return out;
}

export default function OrdersByDayPage() {
  const [from, to0] = monthRange();
  const [dateFrom, setDateFrom] = useState(from);
  const [dateTo, setDateTo] = useState(to0);
  const [levels, setLevels] = useState(['day', '', '', '']);
  const [client, setClient] = useState('');
  const [manager, setManager] = useState('');
  const [product, setProduct] = useState('');
  const [withCancelled, setWithCancelled] = useState(false);
  const [opts, setOpts] = useState({ clients: [], managers: [], products: [] });
  const [data, setData] = useState(null);
  const [used, setUsed] = useState(null);       // параметры, по которым построен результат
  const [open, setOpen] = useState(new Set());
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    ordersByDayAPI.options().then(r => setOpts(r.data)).catch(() => {});
  }, []);

  const params = useCallback(() => {
    const group_by = [];
    for (const l of levels) { if (!l) break; group_by.push(l); }
    return {
      date_from: dateFrom, date_to: dateTo, group_by,
      client, manager, product,
      include_cancelled: withCancelled ? 1 : '',
    };
  }, [levels, dateFrom, dateTo, client, manager, product, withCancelled]);

  const run = async () => {
    setBusy(true);
    const p = params();
    try {
      const r = await ordersByDayAPI.report(p);
      setData(r.data);
      setUsed(p);
      // верхний уровень раскрыт, глубже — по клику
      setOpen(new Set((r.data.children || []).map(n => '/' + n.key)));
    } catch (e) {
      toast.error(t('Не удалось сформировать отчёт'));
    } finally {
      setBusy(false);
    }
  };

  const exportXlsx = async () => {
    try {
      await ordersByDayAPI.download(used || params());
    } catch (e) {
      toast.error(t('Не удалось выгрузить в Excel'));
    }
  };

  const toggle = (path) => setOpen(prev => {
    const n = new Set(prev);
    if (n.has(path)) n.delete(path); else n.add(path);
    return n;
  });

  const setLevel = (i, v) => setLevels(prev => {
    const n = [...prev];
    n[i] = v;
    // выбранный в другом уровне разрез не повторяем
    for (let j = 0; j < n.length; j++) if (j !== i && v && n[j] === v) n[j] = '';
    return n;
  });

  const hasGroups = data && data.children;
  const tot = data && data.totals;

  return (
    <Layout title={t('Заказы по дням')}>
      <div style={{ ...CARD, marginBottom: 14 }}>
        <div style={{
          display: 'grid', gap: 12,
          gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
        }}>
          <label>
            <span style={LBL}>{t('С')}</span>
            <input type="date" style={{ ...FIELD, width: '100%', boxSizing: 'border-box' }}
                   value={dateFrom} onChange={e => setDateFrom(e.target.value)} />
          </label>
          <label>
            <span style={LBL}>{t('По')}</span>
            <input type="date" style={{ ...FIELD, width: '100%', boxSizing: 'border-box' }}
                   value={dateTo} onChange={e => setDateTo(e.target.value)} />
          </label>
        </div>

        <div style={{ ...LBL, marginTop: 14 }}>{t('Группировка (по порядку вложенности)')}</div>
        <div style={{
          display: 'grid', gap: 10,
          gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
        }}>
          {levels.map((v, i) => (
            <select key={i} style={{ ...FIELD, width: '100%' }} value={v}
                    disabled={i > 0 && !levels[i - 1]}
                    onChange={e => setLevel(i, e.target.value)}>
              <option value="">{i === 0 ? t('Без группировки') : t('— нет —')}</option>
              {DIMS.map(d => (
                <option key={d.id} value={d.id}>{i + 1}. {t(d.name)}</option>
              ))}
            </select>
          ))}
        </div>

        <div style={{
          display: 'grid', gap: 10, marginTop: 14,
          gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
        }}>
          <label>
            <span style={LBL}>{t('Контрагент')}</span>
            <select style={{ ...FIELD, width: '100%' }} value={client}
                    onChange={e => setClient(e.target.value)}>
              <option value="">{t('Все')}</option>
              {opts.clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </label>
          <label>
            <span style={LBL}>{t('Менеджер')}</span>
            <select style={{ ...FIELD, width: '100%' }} value={manager}
                    onChange={e => setManager(e.target.value)}>
              <option value="">{t('Все')}</option>
              {opts.managers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </label>
          <label>
            <span style={LBL}>{t('ЛДСП')}</span>
            <select style={{ ...FIELD, width: '100%' }} value={product}
                    onChange={e => setProduct(e.target.value)}>
              <option value="">{t('Все')}</option>
              {opts.products.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </label>
        </div>

        <label style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 12, fontSize: 14 }}>
          <input type="checkbox" checked={withCancelled}
                 onChange={e => setWithCancelled(e.target.checked)} />
          {t('Включая отменённые')}
        </label>

        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 14 }}>
          <button className="touch-btn" onClick={run} disabled={busy} style={{
            background: 'var(--accent)', color: '#fff', border: 'none',
            borderRadius: 10, padding: '10px 20px', fontWeight: 700,
            fontSize: 14.5, cursor: 'pointer', fontFamily: 'inherit',
          }}>{busy ? t('Формируем…') : t('Сформировать')}</button>
          <button className="touch-btn" onClick={exportXlsx} disabled={!data} style={{
            background: 'var(--card)', color: 'var(--text)',
            border: '1px solid var(--border)', borderRadius: 10,
            padding: '10px 20px', fontWeight: 600, fontSize: 14.5,
            cursor: data ? 'pointer' : 'default', fontFamily: 'inherit',
            opacity: data ? 1 : 0.5,
          }}>{t('Выгрузить в Excel')}</button>
          {hasGroups && (
            <>
              <button onClick={() => setOpen(new Set(collectPaths(data.children, '', [])))}
                      style={{ ...FIELD, cursor: 'pointer' }}>{t('Раскрыть всё')}</button>
              <button onClick={() => setOpen(new Set())}
                      style={{ ...FIELD, cursor: 'pointer' }}>{t('Свернуть всё')}</button>
            </>
          )}
        </div>
      </div>

      {data && (
        <div style={CARD}>
          <div style={{
            display: 'flex', gap: 18, flexWrap: 'wrap', marginBottom: 12, fontSize: 14,
          }}>
            <span><b>{tot.orders}</b> {t('заказов')}</span>
            <span><b>{tot.items}</b> {t('позиций')}</span>
            <span><b>{tot.quantity}</b> {t('листов')}</span>
            <span><b>{tot.done}</b> {t('выполнено')}</span>
            <span><b>{tot.shipped}</b> {t('отгружено')}</span>
          </div>
          {tot.items === 0 ? (
            <div style={{ color: 'var(--muted)', padding: 12 }}>
              {t('За выбранный период заказов нет')}
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: 880 }}>
                <thead>
                  <tr>
                    <th style={TH}>{t('Дата и время')}</th>
                    <th style={TH}>{t('№ заказа')}</th>
                    <th style={TH}>{t('Контрагент')}</th>
                    <th style={TH}>{t('Менеджер')}</th>
                    <th style={TH}>{t('ЛДСП · текстура')}</th>
                    <th style={{ ...TH, ...NUM }}>{t('Кол-во')}</th>
                    <th style={{ ...TH, ...NUM }}>{t('Выполнено')}</th>
                    <th style={{ ...TH, ...NUM }}>{t('Отгружено')}</th>
                    <th style={TH}>{t('Статус')}</th>
                  </tr>
                </thead>
                <tbody>
                  {hasGroups
                    ? data.children.map(n => (
                      <Node key={n.key} node={n} depth={0} path={'/' + n.key}
                            open={open} toggle={toggle} />))
                    : data.rows.map((r, i) => <ItemRow key={i} r={r} depth={0} />)}
                  <tr style={{ background: 'var(--bg)' }}>
                    <td colSpan={5} style={{ ...TD, fontWeight: 700 }}>{t('ИТОГО')}</td>
                    <td style={{ ...TD, ...NUM, fontWeight: 700 }}>{tot.quantity}</td>
                    <td style={{ ...TD, ...NUM, fontWeight: 700 }}>{tot.done}</td>
                    <td style={{ ...TD, ...NUM, fontWeight: 700 }}>{tot.shipped}</td>
                    <td style={{ ...TD, fontWeight: 700, whiteSpace: 'nowrap' }}>
                      {t('Заказов')}: {tot.orders} · {t('Позиций')}: {tot.items}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </Layout>
  );
}
